import { useState, useEffect, useCallback } from 'react'
import type { CardType } from '@/utils/urlState'

export type Screen = 'list' | 'unit-detail' | 'plan-detail' | 'sandbox'

export interface HashRoute {
	screen: Screen
	id?: string
	/** Raw query string (no leading `?`) carrying list-screen state. */
	query: string
}

const VALID_ID = /^[a-zA-Z0-9_-]+$/

/** Parse a Spaces route hash, or return null when it's a host-page anchor. */
function matchRoute (hash: string): HashRoute | null {
	const raw = hash.replace(/^#/, '')
	const qIndex = raw.indexOf('?')
	const path = qIndex === -1 ? raw : raw.slice(0, qIndex)
	const query = qIndex === -1 ? '' : raw.slice(qIndex + 1)

	if (!path) return { screen: 'list', query }

	const parts = path.split('/')
	const id = parts[1] && VALID_ID.test(parts[1]) ? parts[1] : undefined
	if (parts[0] === 'unit' && id) return { screen: 'unit-detail', id, query }
	if (parts[0] === 'plan' && id) return { screen: 'plan-detail', id, query }
	if (parts[0] === 'sandbox') return { screen: 'sandbox', query }

	return null
}

export function isSpacesHash (hash: string): boolean {
	return matchRoute(hash) !== null
}

function parseHash (hash: string): HashRoute {
	return matchRoute(hash) ?? { screen: 'list', query: '' }
}

function buildHash (screen: Screen, id: string | undefined, query: string): string {
	let path: string
	switch (screen) {
		case 'unit-detail': path = `unit/${id ?? ''}`; break
		case 'plan-detail': path = `plan/${id ?? ''}`; break
		case 'sandbox': path = 'sandbox'; break
		default: path = ''
	}
	const q = query ? `?${query}` : ''
	if (!path && !q) return ''
	return `#${path}${q}`
}

/** History-state key counting how many detail entries sit above the list entry. */
const DEPTH_KEY = 'spacesDetailDepth'

function currentDetailDepth (): number {
	const depth = (history.state as Record<string, unknown> | null)?.[DEPTH_KEY]
	return typeof depth === 'number' ? depth : 0
}

/** History-state key marking a community view entry pushed from the all-properties list. */
export const COMMUNITY_HISTORY_KEY = 'spacesCommunity'

export interface CommunityHistoryEntry {
	returnTab: CardType
	returnQuery: string
}

export function currentCommunityEntry (): CommunityHistoryEntry | undefined {
	return (history.state as Record<string, unknown> | null)?.[COMMUNITY_HISTORY_KEY] as CommunityHistoryEntry | undefined
}

export function useHashRoute () {
	const [route, setRoute] = useState<HashRoute>(() => parseHash(window.location.hash))

	useEffect(() => {
		function syncFromLocation () {
			if (!isSpacesHash(window.location.hash)) {
				// Keep list state on the list; leave detail/sandbox (e.g. Back to an anchor entry).
				setRoute(prev => prev.screen === 'list' ? prev : { screen: 'list', query: '' })
				return
			}
			const next = parseHash(window.location.hash)
			setRoute(prev =>
				prev.screen === next.screen && prev.id === next.id && prev.query === next.query ? prev : next
			)
		}
		// popstate covers traversals some browsers don't pair with a hashchange.
		window.addEventListener('hashchange', syncFromLocation)
		window.addEventListener('popstate', syncFromLocation)
		return () => {
			window.removeEventListener('hashchange', syncFromLocation)
			window.removeEventListener('popstate', syncFromLocation)
		}
	}, [])

	const navigate = useCallback((screen: Screen, id?: string) => {
		// Preserve the current list-state query when switching screens so that
		// returning from a detail view restores the active filters/sort/view.
		const current = parseHash(window.location.hash)
		const hash = buildHash(screen, id, current.query)
		if (hash) {
			if (hash === window.location.hash) return
			const depth = current.screen === 'list' ? 0 : currentDetailDepth()
			window.location.hash = hash
			if (screen === 'unit-detail' || screen === 'plan-detail') {
				history.replaceState({ [DEPTH_KEY]: depth + 1 }, '')
			}
		} else {
			// Clear the hash without adding a history entry for the bare '#'
			history.pushState(null, '', window.location.pathname + window.location.search)
			setRoute({ screen: 'list', query: '' })
		}
	}, [])

	/**
	 * Return to the list by stepping back over the detail entries, so the browser
	 * Back button doesn't then reopen the detail. Deep-linked details have no list
	 * entry behind them, so the current entry is replaced with the list instead.
	 */
	const backToList = useCallback(() => {
		const depth = currentDetailDepth()
		if (depth > 0) {
			history.go(-depth)
			return
		}
		const { query } = parseHash(window.location.hash)
		history.replaceState(null, '', window.location.pathname + window.location.search + (query ? `#?${query}` : ''))
		setRoute({ screen: 'list', query })
	}, [])

	/** Push a new list-screen history entry carrying `query` (and optional history state). */
	const pushListQuery = useCallback((query: string, state: Record<string, unknown> | null = null) => {
		history.pushState(state, '', window.location.pathname + window.location.search + (query ? `#?${query}` : ''))
		// pushState fires no hashchange/popstate, so sync the route directly.
		setRoute({ screen: 'list', query })
	}, [])

	return { route, navigate, backToList, pushListQuery }
}
